import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { partnerMaterialKeys } from '@/api/query-keys';
import { uploadToPresignedUrl } from '@/lib/file-utils';
import {
  confirmPartnerMaterialUploadContract,
  createPartnerMaterialContract,
  deletePartnerMaterialContract,
  deletePartnerMaterialFileContract,
  requestPartnerMaterialUploadContract,
  updatePartnerMaterialContract,
  type ConfirmPartnerMaterialUploadResponse,
  type CreatePartnerMaterialRequest,
  type CreatePartnerMaterialResponse,
  type DeletePartnerMaterialFileResponse,
  type DeletePartnerMaterialResponse,
  type PartnerMaterialFileFormat,
  type PartnerMaterialLocale,
  type RequestPartnerMaterialUploadRequest,
  type RequestPartnerMaterialUploadResponse,
  type UpdatePartnerMaterialRequest,
  type UpdatePartnerMaterialResponse
} from '@bliks-io/contracts';

const filePath = (path: string, id: string, locale: PartnerMaterialLocale) =>
  path.replace(':id', id).replace(':locale', locale);

function useInvalidateMaterials() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: partnerMaterialKeys.all });
}

export function useCreatePartnerMaterial() {
  const invalidate = useInvalidateMaterials();
  return useMutation({
    mutationFn: async (data: CreatePartnerMaterialRequest) => {
      const { data: response } = await apiClient.post<CreatePartnerMaterialResponse>(
        createPartnerMaterialContract.path,
        data
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

export function useUpdatePartnerMaterial() {
  const invalidate = useInvalidateMaterials();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePartnerMaterialRequest }) => {
      const { data: response } = await apiClient.patch<UpdatePartnerMaterialResponse>(
        updatePartnerMaterialContract.path.replace(':id', id),
        data
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

export function useDeletePartnerMaterial() {
  const invalidate = useInvalidateMaterials();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<DeletePartnerMaterialResponse>(
        deletePartnerMaterialContract.path.replace(':id', id)
      );
      return data;
    },
    onSuccess: invalidate
  });
}

/** Presign → direct upload to storage → confirm, for one language of a file material. */
export function useUploadPartnerMaterialFile() {
  const invalidate = useInvalidateMaterials();
  return useMutation({
    mutationFn: async ({
      id,
      locale,
      format,
      file
    }: {
      id: string;
      locale: PartnerMaterialLocale;
      format: PartnerMaterialFileFormat;
      file: File;
    }) => {
      const { data: presigned } = await apiClient.post<RequestPartnerMaterialUploadResponse>(
        filePath(requestPartnerMaterialUploadContract.path, id, locale),
        { format } satisfies RequestPartnerMaterialUploadRequest
      );
      await uploadToPresignedUrl(presigned.url, presigned.fields, file);
      const { data: response } = await apiClient.post<ConfirmPartnerMaterialUploadResponse>(
        filePath(confirmPartnerMaterialUploadContract.path, id, locale),
        { key: presigned.key, fileName: file.name }
      );
      return response.data;
    },
    onSuccess: invalidate
  });
}

export function useRemovePartnerMaterialFile() {
  const invalidate = useInvalidateMaterials();
  return useMutation({
    mutationFn: async ({ id, locale }: { id: string; locale: PartnerMaterialLocale }) => {
      const { data } = await apiClient.delete<DeletePartnerMaterialFileResponse>(
        filePath(deletePartnerMaterialFileContract.path, id, locale)
      );
      return data.data;
    },
    onSuccess: invalidate
  });
}
